import { useState, useEffect } from 'react';
import { Layout } from 'components/Layout';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Card } from 'components/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Badge } from 'components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Plus, Trash2, Star, Key, Info, UserRound, SlidersHorizontal, ShieldCheck, Save } from 'lucide-react';
import { useToast } from 'hooks/use-toast';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";
import axios from 'utils/axiosConfig';
import { languages } from 'data/languages';

// Types
type ElevenLabsKeyLimits = {
  character_count: number;
  character_limit: number;
  character_remaining: number;
  status: string;
  tier: string;
};

type ElevenLabsKeyOut = {
  id: number;
  label: string;
  is_primary: boolean;
  created_at: string;
  limits: ElevenLabsKeyLimits | null;
};

type Profile = { id: number; email: string; name?: string | null; is_verified: boolean };
type Preferences = {
  default_language?: string | null;
  default_voice_id?: string | null;
  default_voice_name?: string | null;
  playback_speed: number;
  email_notifications: boolean;
  prompt_context?: string | null;
};
type VoiceOption = { id: string; name: string };

export function SettingsPage() {
  const { toast } = useToast();
  const [keys, setKeys] = useState<ElevenLabsKeyOut[]>([]);
  const [loading, setLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileName, setProfileName] = useState('');
  const [preferences, setPreferences] = useState<Preferences>({ playback_speed: 1, email_notifications: true, prompt_context: '' });
  const [availableVoices, setAvailableVoices] = useState<VoiceOption[]>([]);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Form state for adding new key
  const [newKeyApiKey, setNewKeyApiKey] = useState('');
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newKeyIsPrimary, setNewKeyIsPrimary] = useState(false);
  const [isAdding, setIsAdding] = useState(false);

  // Fetch keys on mount
  useEffect(() => {
    fetchKeys();
    Promise.all([
      axios.get<Profile>('/v2/settings/profile'),
      axios.get<Preferences>('/v2/settings/preferences'),
    ]).then(([profileResponse, preferencesResponse]) => {
      setProfile(profileResponse.data);
      setProfileName(profileResponse.data.name || '');
      setPreferences(preferencesResponse.data);
    }).catch(() => {
      toast({ title: "Could not load profile settings", description: "Please refresh and try again.", variant: "destructive" });
    });
    axios.get<VoiceOption[]>('/v2/user/languages').then((response) => setAvailableVoices(response.data)).catch(() => setAvailableVoices([]));
  }, []);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const response = await axios.patch<Profile>('/v2/settings/profile', { name: profileName });
      setProfile(response.data);
      localStorage.setItem('userName', response.data.name || 'User');
      toast({ title: "Profile saved" });
    } catch (error) {
      toast({ title: "Could not save profile", description: "Please try again.", variant: "destructive" });
    } finally {
      setSavingProfile(false);
    }
  };

  const savePreferences = async () => {
    setSavingPreferences(true);
    try {
      const response = await axios.patch<Preferences>('/v2/settings/preferences', preferences);
      setPreferences(response.data);
      toast({ title: "Preferences saved" });
    } catch (error) {
      toast({ title: "Could not save preferences", description: "Please try again.", variant: "destructive" });
    } finally {
      setSavingPreferences(false);
    }
  };

  const changePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords do not match", description: "Enter the same new password twice.", variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post('/v2/settings/password', { current_password: currentPassword, new_password: newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast({ title: "Password updated" });
    } catch (error: any) {
      toast({ title: "Could not update password", description: error.response?.data?.detail || "Please check your current password and try again.", variant: "destructive" });
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const response = await axios.get<ElevenLabsKeyOut[]>('/v2/settings/elevenlabs-keys');
      setKeys(response.data);
    } catch (error) {
      console.error('Error fetching ElevenLabs keys:', error);
      toast({
        title: "Error",
        description: "Failed to load ElevenLabs API keys.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddKey = async () => {
    if (!newKeyApiKey.trim() || !newKeyLabel.trim()) {
      toast({
        title: "Validation error",
        description: "Please provide both API key and label.",
        variant: "destructive"
      });
      return;
    }

    setIsAdding(true);
    try {
      await axios.post('/v2/settings/elevenlabs-keys', {
        api_key: newKeyApiKey,
        label: newKeyLabel,
        is_primary: keys.length === 0 || newKeyIsPrimary
      });
      
      toast({
        title: "Success",
        description: "ElevenLabs API key added successfully."
      });
      
      // Reset form and close dialog
      setNewKeyApiKey('');
      setNewKeyLabel('');
      setNewKeyIsPrimary(false);
      setIsDialogOpen(false);
      
      // Refresh keys list
      await fetchKeys();
    } catch (error: any) {
      console.error('Error adding key:', error);
      const errorMsg = error.response?.data?.detail || "Failed to add API key. Please check if the key is valid.";
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleSetPrimary = async (keyId: number) => {
    try {
      await axios.patch(`/v2/settings/elevenlabs-keys/${keyId}`, {
        is_primary: true
      });
      
      toast({
        title: "Success",
        description: "Primary API key updated."
      });
      
      await fetchKeys();
    } catch (error) {
      console.error('Error setting primary key:', error);
      toast({
        title: "Error",
        description: "Failed to update primary key.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteKey = async (keyId: number) => {
    if (!window.confirm('Are you sure you want to delete this API key? Voices added from this key may no longer be accessible.')) {
      return;
    }

    try {
      await axios.delete(`/v2/settings/elevenlabs-keys/${keyId}`);
      
      toast({
        title: "Success",
        description: "API key deleted successfully."
      });
      
      await fetchKeys();
    } catch (error) {
      console.error('Error deleting key:', error);
      toast({
        title: "Error",
        description: "Failed to delete API key.",
        variant: "destructive"
      });
    }
  };

  const formatNumber = (num: number) => {
    return num.toLocaleString();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <Layout>
      <div className="min-h-screen px-4 py-12 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Title */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-700">Settings</h1>
            <p className="text-gray-600 mt-2">Make Divine Echo feel like yours.</p>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <div className="mb-5 flex items-center gap-2"><UserRound className="w-5 h-5 text-gray-600" /><h2 className="text-xl font-semibold text-gray-700">Profile</h2></div>
              <div className="space-y-4">
                <div className="space-y-2"><Label htmlFor="profile-name">Your name</Label><Input id="profile-name" value={profileName} onChange={(event) => setProfileName(event.target.value)} placeholder="How should we call you?" maxLength={120} /></div>
                <div className="space-y-2"><Label>Email address</Label><Input value={profile?.email || ''} disabled /><p className="text-xs text-gray-500">Email changes will be added with verification.</p></div>
                <Button onClick={saveProfile} disabled={savingProfile} className="gap-2"><Save size={15} />{savingProfile ? 'Saving...' : 'Save profile'}</Button>
              </div>
            </Card>

            <Card>
              <div className="mb-5 flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-gray-600" /><h2 className="text-xl font-semibold text-gray-700">Preferences</h2></div>
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2"><div className="space-y-2"><Label htmlFor="default-language">Default language</Label><Select value={preferences.default_language || '__none__'} onValueChange={(value) => setPreferences({ ...preferences, default_language: value === '__none__' ? null : value })}><SelectTrigger id="default-language"><SelectValue placeholder="Choose a language" /></SelectTrigger><SelectContent><SelectItem value="__none__" disabled>Choose a language</SelectItem>{languages.map((language) => <SelectItem key={language.code} value={language.code}>{language.name}</SelectItem>)}</SelectContent></Select></div><div className="space-y-2"><Label htmlFor="default-voice">Default voice</Label><Select value={availableVoices.some((voice) => voice.id === preferences.default_voice_id) ? preferences.default_voice_id as string : '__none__'} onValueChange={(value) => { const voice = availableVoices.find((item) => item.id === value); setPreferences({ ...preferences, default_voice_id: value === '__none__' ? null : value, default_voice_name: voice?.name || null }); }}><SelectTrigger id="default-voice"><SelectValue placeholder="Choose a voice" /></SelectTrigger><SelectContent><SelectItem value="__none__" disabled>{availableVoices.length ? 'Choose a voice' : 'Add voices on the Voices page'}</SelectItem>{availableVoices.map((voice) => <SelectItem key={voice.id} value={voice.id}>{voice.name}</SelectItem>)}</SelectContent></Select></div></div>
                <div className="space-y-2"><Label htmlFor="playback-speed">Playback speed: {preferences.playback_speed}×</Label><input id="playback-speed" type="range" min="0.5" max="2" step="0.1" value={preferences.playback_speed} onChange={(event) => setPreferences({ ...preferences, playback_speed: Number(event.target.value) })} className="w-full accent-emerald-700" /></div>
                <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={preferences.email_notifications} onChange={(event) => setPreferences({ ...preferences, email_notifications: event.target.checked })} className="h-4 w-4" />Email me when projects finish</label>
                <div className="space-y-2"><Label htmlFor="prompt-context">Prompt context</Label><textarea id="prompt-context" value={preferences.prompt_context || ''} onChange={(event) => setPreferences({ ...preferences, prompt_context: event.target.value })} maxLength={2000} rows={4} placeholder="Optional: describe your audience, preferred terminology, or tone…" className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /><p className="text-xs text-gray-500">This context is added to future translation prompts. It cannot change the required output format or safety rules.</p></div>
                <Button onClick={savePreferences} disabled={savingPreferences} variant="outline" className="gap-2"><Save size={15} />{savingPreferences ? 'Saving...' : 'Save preferences'}</Button>
              </div>
            </Card>
          </div>

          <Card>
            <div className="mb-5 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-gray-600" /><div><h2 className="text-xl font-semibold text-gray-700">Security</h2><p className="text-sm text-gray-500">Keep your account protected.</p></div></div>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2"><Label htmlFor="current-password">Current password</Label><Input id="current-password" type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" /></div>
              <div className="space-y-2"><Label htmlFor="new-password">New password</Label><Input id="new-password" type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" minLength={8} /></div>
              <div className="space-y-2"><Label htmlFor="confirm-password">Confirm new password</Label><Input id="confirm-password" type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" minLength={8} /></div>
            </div>
            <Button onClick={changePassword} disabled={changingPassword || !currentPassword || newPassword.length < 8 || !confirmPassword} variant="outline" className="mt-4">{changingPassword ? 'Updating...' : 'Change password'}</Button>
          </Card>

          {/* ElevenLabs Keys Section */}
          <Card>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <Key className="w-6 h-6 text-gray-600" />
                <div><h2 className="text-2xl font-semibold text-gray-700">Integrations</h2><p className="text-sm font-normal text-gray-500">ElevenLabs voice provider</p></div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info size={18} className="text-muted-foreground cursor-pointer" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm">
                      <p>Add multiple ElevenLabs API keys to access professional voice clones from different accounts. The primary key is used by default.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              
              <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="flex items-center gap-2">
                    <Plus size={16} />
                    Add API Key
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add ElevenLabs API Key</DialogTitle>
                    <DialogDescription>
                      Enter your ElevenLabs API key and a label to identify it.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="api-key">API Key</Label>
                      <Input
                        id="api-key"
                        type="password"
                        placeholder="sk_..."
                        value={newKeyApiKey}
                        onChange={(e) => setNewKeyApiKey(e.target.value)}
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="label">Label</Label>
                      <Input
                        id="label"
                        placeholder="e.g., Main Account, Work Account"
                        value={newKeyLabel}
                        onChange={(e) => setNewKeyLabel(e.target.value)}
                      />
                    </div>

                    {keys.length > 0 && (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="is-primary"
                          checked={newKeyIsPrimary}
                          onChange={(e) => setNewKeyIsPrimary(e.target.checked)}
                          className="w-4 h-4"
                        />
                        <Label htmlFor="is-primary" className="cursor-pointer">
                          Set as primary key
                        </Label>
                      </div>
                    )}
                  </div>
                  
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsDialogOpen(false)}
                      disabled={isAdding}
                    >
                      Cancel
                    </Button>
                    <Button onClick={handleAddKey} disabled={isAdding}>
                      {isAdding ? 'Adding...' : 'Add Key'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : keys.length === 0 ? (
              <div className="text-center py-20">
                <Key className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-700 mb-2">No API Keys Added</h3>
                <p className="text-gray-500 mb-6">
                  Add your first ElevenLabs API key to start using professional voice clones.
                </p>
              </div>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Label</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tier</TableHead>
                      <TableHead>Characters Used</TableHead>
                      <TableHead>Characters Remaining</TableHead>
                      <TableHead>Added</TableHead>
                      <TableHead className="w-[120px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {keys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {key.is_primary && (
                              <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                            )}
                            <span className="font-medium">{key.label}</span>
                            {key.is_primary && (
                              <Badge variant="default" className="text-xs">Primary</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {key.limits ? (
                            <Badge 
                              variant={key.limits.status === 'active' ? 'default' : 'destructive'}
                            >
                              {key.limits.status}
                            </Badge>
                          ) : (
                            <Badge variant="outline">Unknown</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {key.limits ? (
                            <Badge variant="outline">{key.limits.tier}</Badge>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {key.limits ? formatNumber(key.limits.character_count) : '-'}
                        </TableCell>
                        <TableCell>
                          {key.limits ? (
                            <span className={
                              key.limits.character_remaining < 1000 
                                ? 'text-red-600 font-semibold' 
                                : 'text-gray-700'
                            }>
                              {formatNumber(key.limits.character_remaining)} / {formatNumber(key.limits.character_limit)}
                            </span>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(key.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {!key.is_primary && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSetPrimary(key.id)}
                                    >
                                      <Star className="w-4 h-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Set as primary
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteKey(key.id)}
                                    className="text-red-600 hover:text-red-900"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Delete key
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Layout>
  );
}

export default SettingsPage;
